import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE as P } from "../tokens";
import { BottomNav } from "../components/BottomNav";
import { useAuth } from "../context/AuthContext";
import { rciApi } from "../api/rci";
import { authApi } from "../api/auth";
import { ouraApi } from "../api/oura";
import { isOuraUIEnabled } from "../utils/features";
import { wearablesApi } from "../api/wearables";
import { presentedScore } from "../utils/presentedScore";
import { DOMAIN_WORD, DOMAIN_META } from "../utils/domainLabels";
import { deletionCompletedDespiteError } from "../utils/accountDeletion";
import { usersApi } from "../api/users";
import { AIDeclineExplainer } from "../components/AIConsentModal";
import { DomainDots } from "../components/DomainSubScores";
import DomainInfoButton from "../components/DomainInfoButton";
import ReferAFriend from "../components/ReferAFriend";
import { Sparkline } from "../components/Sparkline";
import { Wordmark } from "../components/Wordmark";
import {
  enablePush,
  isSupported as isPushSupported,
  unsubscribe as unsubscribePush,
} from "../services/pushNotifications";
import { pushApi } from "../api/push";

// ── RCI History Chart ─────────────────────────────────────────────────────────
function RCIChart({ history }) {
  const W = 340, H = 140;
  const PAD = { top: 14, right: 12, bottom: 28, left: 28 };
  const iW = W - PAD.left - PAD.right;
  const iH = H - PAD.top - PAD.bottom;

  const scores = history.map(d => d.rci);
  const min = Math.min(...scores) - 8;
  const max = Math.max(...scores) + 8;

  const x = i => PAD.left + (i / (history.length - 1)) * iW;
  const y = v => PAD.top + iH - ((v - min) / (max - min)) * iH;

  const linePts = history.map((d, i) => `${x(i)},${y(d.rci)}`).join(" ");
  const areaPath = [
    `M ${x(0)},${y(history[0].rci)}`,
    ...history.map((d, i) => `L ${x(i)},${y(d.rci)}`),
    `L ${x(history.length - 1)},${PAD.top + iH}`,
    `L ${x(0)},${PAD.top + iH}`, "Z",
  ].join(" ");

  const [hovered, setHovered] = useState(null);
  const labelInterval = Math.ceil(history.length / 5);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id="rciAreaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={P.primary} stopOpacity="0.13" />
          <stop offset="100%" stopColor={P.primary} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map((t, i) => {
        const yv = PAD.top + iH * (1 - t);
        const val = Math.round(min + t * (max - min));
        return (
          <g key={i}>
            <line x1={PAD.left} y1={yv} x2={PAD.left + iW} y2={yv}
              stroke={P.border} strokeWidth="1" strokeDasharray="3 4" />
            <text x={PAD.left - 5} y={yv + 4} fontSize="9" fill={P.textFaint}
              textAnchor="end" fontFamily="DM Sans, sans-serif">{val}</text>
          </g>
        );
      })}
      <path d={areaPath} fill="url(#rciAreaGrad)" />
      <polyline points={linePts} fill="none" stroke={P.primary} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
      {history.map((d, i) => (
        <g key={i}>
          {i % labelInterval === 0 && (
            <text x={x(i)} y={H - 4} fontSize="8.5"
              fill={hovered === i ? P.primary : P.textFaint}
              textAnchor="middle" fontFamily="DM Sans, sans-serif">{d.day}</text>
          )}
          <rect x={x(i) - 12} y={PAD.top} width={24} height={iH}
            fill="transparent" style={{ cursor: "pointer" }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)} />
          <circle cx={x(i)} cy={y(d.rci)} r={hovered === i ? 5 : 2.5}
            fill={hovered === i ? P.primary : P.surface}
            stroke={P.primary} strokeWidth="1.8"
            style={{ transition: "r 0.15s" }} />
          {hovered === i && (
            <g>
              <rect
                x={Math.min(Math.max(x(i) - 32, PAD.left), PAD.left + iW - 64)}
                y={y(d.rci) - 32} width={64} height={22} rx={6} fill={P.dark} />
              <text
                x={Math.min(Math.max(x(i), PAD.left + 32), PAD.left + iW - 32)}
                y={y(d.rci) - 17} fontSize="10" fill={P.bg}
                textAnchor="middle" fontFamily="DM Sans, sans-serif">
                {d.rci} · {d.day}
              </text>
            </g>
          )}
        </g>
      ))}
    </svg>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ value, onChange, disabled = false }) {
  return (
    <button
      onClick={() => { if (!disabled) onChange(!value); }}
      disabled={disabled}
      aria-pressed={value ? "true" : "false"}
      style={{
        width: 42, height: 24, borderRadius: 99,
        background: value ? P.primary : P.border,
        border: "none",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        position: "relative",
        transition: "background 0.2s, opacity 0.2s", flexShrink: 0,
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: "50%", background: P.surface,
        position: "absolute", top: 3,
        left: value ? 21 : 3,
        transition: "left 0.2s",
        boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
      }} />
    </button>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .profile-screen {
    font-family: 'DM Sans', sans-serif;
    background: ${P.bg};
    min-height: 100vh;
    color: ${P.text};
    max-width: 420px;
    margin: 0 auto;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .fu  { animation: fadeUp 0.5s ease forwards; opacity: 0; }
  .d1  { animation-delay: 0.05s; }
  .d2  { animation-delay: 0.15s; }
  .d3  { animation-delay: 0.25s; }
  .d4  { animation-delay: 0.35s; }
  .d5  { animation-delay: 0.45s; }
  .d6  { animation-delay: 0.55s; }
  .d7  { animation-delay: 0.65s; }
  .d8  { animation-delay: 0.75s; }

  .section-card {
    background: ${P.surface};
    border-radius: 20px;
    padding: 22px;
    box-shadow: 0 1px 16px rgba(28,24,20,0.07);
    margin-bottom: 12px;
  }

  /* Elevated treatment for the "Data connections" hub — warmer tint
     to visually separate it from flat section cards like Notifications. */
  .data-connections-card {
    background: linear-gradient(135deg, rgba(158,132,86,0.09) 0%, rgba(158,132,86,0.03) 100%);
    border: 1px solid rgba(158,132,86,0.12);
    border-radius: 20px;
    padding: 22px;
    box-shadow: 0 1px 16px rgba(28,24,20,0.07);
    margin-bottom: 12px;
  }

  .data-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 10px 12px;
    margin: 0 -12px;
    background: transparent;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    text-align: left;
    font-family: 'DM Sans', sans-serif;
    color: ${P.text};
    transition: background 0.15s;
  }
  .data-row:hover { background: rgba(158,132,86,0.06); }
  .data-row + .data-row { margin-top: 4px; }

  .data-row-add {
    color: ${P.textMute};
    font-weight: 400;
    border: 1px dashed rgba(158,132,86,0.28);
    padding: 12px;
    margin: 10px 0 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 12.5px;
  }
  .data-row-add:hover { color: ${P.primary}; border-color: rgba(158,132,86,0.45); }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 99px;
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.04em;
    font-family: 'DM Sans', sans-serif;
    white-space: nowrap;
  }
  .status-pill.pill-connected {
    background: rgba(122, 160, 120, 0.12);
    color: #5F8059;
  }
  .status-pill.pill-muted {
    background: rgba(28,24,20,0.06);
    color: ${P.textMute};
  }
  .status-pill .dot {
    width: 6px; height: 6px; border-radius: 50%;
  }
  .status-pill.pill-connected .dot { background: #7AA078; }
  /* Reconnect prompt: canonical bronze, distinct from green Connected
     and grey Not connected. (Wearables.jsx's amber is not a token.) */
  .status-pill.pill-warn {
    background: ${P.primarySoft};
    color: ${P.primary};
  }
  .status-pill.pill-warn .dot { background: ${P.primary}; }
  .status-pill.pill-muted .dot { background: ${P.textFaint}; }

  .section-label {
    font-size: 10px;
    color: ${P.textMute};
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-weight: 500;
    margin-bottom: 16px;
  }

  .notif-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid ${P.border};
  }
  .notif-row:last-child { border-bottom: none; }

  .btn-logout {
    width: 100%; padding: 15px;
    background: none;
    border: 1.5px solid ${P.border};
    border-radius: 14px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px; font-weight: 400;
    color: ${P.textMid};
    transition: all 0.15s;
    margin-bottom: 10px;
  }
  .btn-logout:hover { border-color: ${P.primaryMid}; color: ${P.primary}; }

  .btn-delete {
    width: 100%; padding: 15px;
    background: none; border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 300;
    color: ${P.textFaint};
    transition: color 0.15s;
  }
  .btn-delete:hover { color: #C0392B; }

  .nav-btn {
    flex: 1; background: none; border: none; cursor: pointer;
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    padding: 8px 4px; transition: opacity 0.15s;
  }
  .nav-btn:hover { opacity: 0.65; }
`;

const SCORE_KEY = { BIO: 'bio_score', PSYCH: 'psych_score', BEH: 'beh_score' };
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function computeStreak(historyArr) {
  if (!historyArr || historyArr.length === 0) return 0;
  const sorted = [...historyArr].sort((a, b) =>
    new Date(b.created_at) - new Date(a.created_at)
  );
  let streak = 1;
  for (let i = 1; i < sorted.length; i++) {
    const curr = new Date(sorted[i - 1].created_at);
    const prev = new Date(sorted[i].created_at);
    curr.setHours(0, 0, 0, 0);
    prev.setHours(0, 0, 0, 0);
    const diffDays = (curr - prev) / (1000 * 60 * 60 * 24);
    if (diffDays <= 1) streak++;
    else break;
  }
  return streak;
}

function computeMemberDays(user) {
  if (!user?.created_at) return '—';
  const created = new Date(user.created_at);
  const now = new Date();
  return Math.max(1, Math.floor((now - created) / (1000 * 60 * 60 * 24)));
}

// ── Main Component ────────────────────────────────────────────────────────────
const WEEKDAYS = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

// ── Data-connections rows ────────────────────────────────────────────
//
// Which providers the Profile summary lists. Previously this was one
// hardcoded Oura row, so a user with Apple Health connected saw nothing
// here at all — the provider was invisible on Profile no matter what
// the server knew. Now the list is driven by GET /wearables/status.

const PROVIDER_ICON = {
  oura: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
  apple_health: <path d="M20.8 5.6a5 5 0 0 0-7.1 0l-1.7 1.7-1.7-1.7a5 5 0 1 0-7.1 7.1l8.8 8.8 8.8-8.8a5 5 0 0 0 0-7.1z" />,
  health_connect: <><path d="M22 12h-4l-3 8-6-16-3 8H2" /></>,
  // A wrist band: Fitbit is a wearable on the wrist, not Oura's ring.
  fitbit: <><rect x="7" y="6" width="10" height="12" rx="3" /><path d="M9 6V3h6v3M9 18v3h6v-3" /></>,
  // Any provider without its own entry: a plain dot, never another brand's mark.
  generic: <circle cx="12" cy="12" r="4" />,
};

// Own-property lookups: a server key such as "constructor" must not
// resolve to an inherited Object member.
function providerIconKey(key) {
  return Object.prototype.hasOwnProperty.call(PROVIDER_ICON, key) ? key : "generic";
}

function providerMetrics(key) {
  return Object.prototype.hasOwnProperty.call(PROVIDER_METRICS, key) ? PROVIDER_METRICS[key] : "";
}

const PROVIDER_METRICS = {
  oura: "HRV · Sleep · Readiness",
  apple_health: "HRV · Sleep · Resting HR",
  health_connect: "HRV · Sleep · Resting HR",
  fitbit: "HRV · Sleep · Resting HR",
};

/**
 * Build the rows to render.
 *
 * Show every genuinely-connected provider, plus any provider whose
 * credentials were rejected (``needs_reconnect``, Fitbit today) so the
 * user sees the prompt to reconnect it. When there is neither,
 * fall back to a single not-connected Oura row so the section still
 * reads as an invitation rather than going blank — that was the prior
 * empty state and it works.
 *
 * ``providers`` is null while the request is in flight; fall back to
 * the legacy Oura-only status so the section doesn't flicker empty.
 */
function DATA_CONNECTION_ROWS(providers, ouraStatus, ouraUI = true) {
  if (!ouraUI) return dataConnectionRowsWithoutOura(providers);
  if (!providers) {
    return [{ key: "oura", label: "Oura", connected: Boolean(ouraStatus?.connected) }];
  }
  const connected = providers.filter((p) => p.connected || p.needs_reconnect);
  if (connected.length) return connected;
  const oura = providers.find((p) => p.key === "oura");
  return [oura || { key: "oura", label: "Oura", connected: false }];
}

/**
 * Same rows with every Oura row removed — the native iOS build while
 * VITE_IOS_OURA_ENABLED is off (App Store 1.0, see utils/features.js).
 * The empty-state invitation becomes Apple Health instead of Oura. An
 * existing Oura connection keeps syncing server-side; it just is not
 * listed here.
 */
function dataConnectionRowsWithoutOura(providers) {
  const list = (providers || []).filter((p) => p.key !== "oura");
  const connected = list.filter((p) => p.connected || p.needs_reconnect);
  if (connected.length) return connected;
  const apple = list.find((p) => p.key === "apple_health");
  return [apple || { key: "apple_health", label: "Apple Health", connected: false }];
}


export default function Profile({ onLogout }) {
  const navigate = useNavigate();
  const { user, token, patchUser } = useAuth();

  const [snapshot, setSnapshot] = useState(null);
  const [historyRaw, setHistoryRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ouraStatus, setOuraStatus] = useState(null);
  // Server-derived connection state for every provider. Replaces the
  // old hardcoded single-Oura row, which meant a connected Apple
  // Health could never appear on this screen at all.
  const [wearableProviders, setWearableProviders] = useState(null);
  const [ouraLoading, setOuraLoading] = useState(false);
  // Two-screen account deletion (App Store Guideline 5.1.1(v)).
  // 0 = idle, 1 = "Delete your account?", 2 = "This is permanent."
  // Only the step-2 button calls the API. Cancel on EITHER step returns
  // straight to 0 — a cancel is never a step backwards into the flow it
  // is trying to leave.
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const cancelDelete = () => {
    setDeleteStep(0);
    setDeleteError("");
  };

  // AI data-sharing consent toggle. Enabling is one tap; disabling
  // routes through the what-changes explainer so the decline is
  // informed, then PATCHes — effective immediately server-side.
  const [aiDeclineConfirming, setAiDeclineConfirming] = useState(false);
  const [aiConsentSaving, setAiConsentSaving] = useState(false);

  const onAIConsentToggle = async (next) => {
    if (aiConsentSaving) return;
    if (!next) {
      setAiDeclineConfirming(true);
      return;
    }
    setAiConsentSaving(true);
    patchUser({ ai_data_consent: true });
    try {
      await usersApi.setAIConsent(true, token);
    } catch {
      // Backend fails closed; a lost opt-in just re-shows the off state
      // on the next refresh.
    } finally {
      setAiConsentSaving(false);
    }
  };

  const confirmAIConsentOff = async () => {
    if (aiConsentSaving) return;
    setAiConsentSaving(true);
    patchUser({ ai_data_consent: false });
    try {
      await usersApi.setAIConsent(false, token);
    } catch {
      // If the PATCH failed the server still holds the previous value;
      // the next /auth/me refresh reconciles the toggle.
    } finally {
      setAiConsentSaving(false);
      setAiDeclineConfirming(false);
    }
  };

  // Research-consent toggle. Tri-state on the server
  // (True / False / NULL = never asked); NULL renders OFF, matching the
  // backend's fail-closed reading — "we never asked" is not a yes. The
  // first interaction writes an explicit boolean, which resolves it.
  // Unlike AI consent there is no decline explainer: the one real side
  // effect (written-entry sharing stops) is stated in the row copy, so
  // the tap is already informed.
  const [researchConsentSaving, setResearchConsentSaving] = useState(false);

  const onResearchConsentToggle = async (next) => {
    if (researchConsentSaving) return;
    setResearchConsentSaving(true);
    patchUser({ research_consent: next });
    try {
      await usersApi.setConsent(next, token);
    } catch {
      // The server still holds the previous value; the next /auth/me
      // refresh reconciles the toggle.
    } finally {
      setResearchConsentSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleting) return;
    setDeleteError("");
    setDeleting(true);
    try {
      await authApi.deleteAccount(token);
      onLogout();
    } catch (err) {
      // A 401/404 here means the account is already gone (client retry
      // replayed the DELETE after a lost response) — finish the logout
      // instead of showing an error over a successful deletion.
      if (deletionCompletedDespiteError(err)) {
        onLogout();
        return;
      }
      setDeleting(false);
      setDeleteError("Something went wrong deleting your account. Please try again.");
    }
  };
  // ``checkin`` mirrors the server-side push subscription. ``insights``
  // and ``protocol`` are local-only for now (per the sprint brief —
  // wire to a user_preferences table later).
  const [notifs, setNotifs] = useState(() => {
    let insights = true;
    let protocol = false;
    try {
      const raw = localStorage.getItem('haiven_notif_prefs');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed.insights === 'boolean') insights = parsed.insights;
        if (typeof parsed.protocol === 'boolean') protocol = parsed.protocol;
      }
    } catch { /* ignore */ }
    return { checkin: false, insights, protocol };
  });
  // Browser-level permission state. Three buckets:
  //   'default' / 'granted' / 'denied' / 'unsupported'
  // Drives whether we render the toggle or the "blocked in browser
  // settings" message.
  const [pushPermission, setPushPermission] = useState('default');
  const [pushBusy, setPushBusy] = useState(false);

  // Per-user reminder times (morning / midday / evening + IANA tz).
  // Mirrors the schema added in migration 20260527_000052. The picker
  // is only revealed when push is on AND permission isn't blocked —
  // showing a time picker that won't fire would be misleading.
  // ``remindersServer`` snapshots the last-saved state so we can show
  // a dirty marker / save button only when the user has made changes.
  const [reminders, setReminders] = useState(() => ({
    morning_time: '07:30',
    midday_time: '12:00',
    evening_time: '20:00',
    morning_enabled: true,
    midday_enabled: false,
    evening_enabled: false,
    timezone: typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : null,
  }));
  const [remindersServer, setRemindersServer] = useState(null);
  const [remindersSaving, setRemindersSaving] = useState(false);
  const [remindersSaveMsg, setRemindersSaveMsg] = useState('');

  // Persist insights + protocol prefs locally on every change. Tiny
  // payload, no need for debouncing.
  useEffect(() => {
    try {
      localStorage.setItem('haiven_notif_prefs', JSON.stringify({
        insights: notifs.insights, protocol: notifs.protocol,
      }));
    } catch { /* ignore */ }
  }, [notifs.insights, notifs.protocol]);

  // On mount: ask the browser its current permission state and the
  // backend whether we have at least one subscription on file for
  // this user. The toggle reads as ON only when BOTH say yes.
  useEffect(() => {
    if (!token) return;
    if (typeof window === 'undefined' || !isPushSupported()) {
      setPushPermission('unsupported');
      return;
    }
    setPushPermission(Notification.permission);
    pushApi.status(token).then(s => {
      setNotifs(prev => ({ ...prev, checkin: !!s?.subscribed }));
    }).catch(() => { /* leave default false */ });
    // Hydrate reminder prefs from the server. Server-side times come
    // back as HH:MM:SS — coerce to HH:MM so <input type="time"> can
    // round-trip them without complaint.
    pushApi.getReminders(token).then(r => {
      if (!r) return;
      const trim = (t) => (typeof t === 'string' ? t.slice(0, 5) : t);
      const hydrated = {
        morning_time: trim(r.morning_time) || '07:30',
        midday_time: trim(r.midday_time) || '12:00',
        evening_time: trim(r.evening_time) || '20:00',
        morning_enabled: !!r.morning_enabled,
        midday_enabled: !!r.midday_enabled,
        evening_enabled: !!r.evening_enabled,
        timezone: r.timezone || (typeof Intl !== 'undefined'
          ? Intl.DateTimeFormat().resolvedOptions().timeZone : null),
      };
      setReminders(hydrated);
      setRemindersServer(hydrated);
    }).catch(() => { /* leave defaults */ });
  }, [token]);

  // Dirty check — drives whether the Save button is enabled.
  const remindersDirty = remindersServer != null
    && JSON.stringify(reminders) !== JSON.stringify(remindersServer);

  const onSaveReminders = async () => {
    if (remindersSaving || !remindersDirty) return;
    setRemindersSaving(true);
    setRemindersSaveMsg('');
    try {
      const saved = await pushApi.updateReminders(reminders, token);
      // Server canonicalizes times back to HH:MM:SS — trim for the UI.
      const trim = (t) => (typeof t === 'string' ? t.slice(0, 5) : t);
      const canon = {
        morning_time: trim(saved.morning_time),
        midday_time: trim(saved.midday_time),
        evening_time: trim(saved.evening_time),
        morning_enabled: !!saved.morning_enabled,
        midday_enabled: !!saved.midday_enabled,
        evening_enabled: !!saved.evening_enabled,
        timezone: saved.timezone || reminders.timezone,
      };
      setReminders(canon);
      setRemindersServer(canon);
      setRemindersSaveMsg('Saved');
      setTimeout(() => setRemindersSaveMsg(''), 2500);
    } catch (e) {
      setRemindersSaveMsg('Could not save — try again');
    } finally {
      setRemindersSaving(false);
    }
  };

  // Care team — "I see a provider" + session day. Stored in
  // profile_json (PATCH /auth/me merges), keys: sees_provider (bool),
  // session_day (0=Sunday … 6=Saturday, JS getDay() numbering, or
  // null = not set). Drives the Home report card's "Session coming
  // up" copy on the day of / day before the session. Saves are
  // fire-and-forget with optimistic local state; a failed PATCH
  // reverts on next login rather than blocking the toggle.
  const profile = user?.profile_json || {};
  const [seesProvider, setSeesProvider] = useState(!!profile.sees_provider);
  const [sessionDay, setSessionDay] = useState(
    typeof profile.session_day === "number" ? profile.session_day : ""
  );

  const saveCareTeam = async (fields) => {
    try {
      await authApi.updateProfile({ profile_json: fields }, token);
      patchUser({ profile_json: { ...(user?.profile_json || {}), ...fields } });
    } catch { /* optimistic UI keeps the local value; server wins on reload */ }
  };

  const onSeesProviderToggle = (next) => {
    setSeesProvider(next);
    const fields = { sees_provider: next };
    if (!next) {
      fields.session_day = null;
      setSessionDay("");
    }
    saveCareTeam(fields);
  };

  const onSessionDayChange = (raw) => {
    const val = raw === "" ? null : Number(raw);
    setSessionDay(raw === "" ? "" : Number(raw));
    saveCareTeam({ session_day: val });
  };

  const onCheckinToggle = async (next) => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (next) {
        const result = await enablePush(token);
        if (result === 'subscribed') {
          setNotifs(prev => ({ ...prev, checkin: true }));
          setPushPermission('granted');
        } else if (result === 'denied') {
          // Browser blocked. Reflect that — there's no recovery path
          // without the user editing site settings.
          setPushPermission('denied');
        } else if (result === 'install-required') {
          // iOS Safari, not yet added to Home Screen — Web Push can't
          // deliver from a bare tab. Recoverable: install then retry,
          // so this doesn't set 'denied'/'unsupported'.
          setPushPermission('install-required');
        }
      } else {
        const ok = await unsubscribePush(token);
        if (ok) setNotifs(prev => ({ ...prev, checkin: false }));
      }
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    Promise.all([
      rciApi.snapshot(user.id, token).catch(() => null),
      rciApi.history(user.id, 30, token).catch(() => []),
      ouraApi.status(token).catch(() => null),
      wearablesApi.status(token).catch(() => null),
    ]).then(([snap, hist, oura, wearables]) => {
      setSnapshot(snap);
      setHistoryRaw(hist || []);
      if (oura) setOuraStatus(oura);
      if (wearables?.providers) setWearableProviders(wearables.providers);
    }).finally(() => setLoading(false));
  }, [user, token]);

  // Handle Oura OAuth callback code in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (code && token) {
      setOuraLoading(true);
      ouraApi.callback(code, state, token)
        .then(status => setOuraStatus(status))
        .catch(() => {})
        .finally(() => {
          setOuraLoading(false);
          // Clean URL
          window.history.replaceState({}, "", window.location.pathname);
        });
    }
  }, [token]);

  const totalSnapshots = snapshot?.total_snapshots || historyRaw.length || 0;
  const streak = computeStreak(historyRaw);
  const memberDays = computeMemberDays(user);
  const fullName = user?.full_name || 'Haiven User';
  const email = user?.email || '';
  const initials = fullName.split(" ").map(n => n[0]).join("").toUpperCase();

  // Build chart data from API history.
  //
  // /history returns DESC (newest first). The Signal History chart
  // reads left-to-right as a timeline, so it needs ASC (oldest first).
  // We sort on a copy so ``historyRaw`` itself stays in DESC order
  // for callers that depend on "most recent first" (computeStreak,
  // average derivation, etc.).
  const chartHistory = historyRaw
    .slice()
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .map(s => {
    const d = new Date(s.created_at);
    return {
      day: `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`,
      rci: presentedScore(s),
    };
  });

  // Domain averages from history
  // Object key order (and therefore the Object.entries render order below)
  // comes from DOMAIN_META, not a hardcoded literal, so it can't drift.
  const domainAvg = (() => {
    if (historyRaw.length === 0) {
      return Object.fromEntries(DOMAIN_META.map((d) => [d.code, 0]));
    }
    const sum = Object.fromEntries(DOMAIN_META.map((d) => [d.code, 0]));
    historyRaw.forEach(s => {
      sum.BIO += s.bio_score || 0;
      sum.PSYCH += s.psych_score || 0;
      sum.BEH += s.beh_score || 0;
    });
    const n = historyRaw.length;
    return Object.fromEntries(
      DOMAIN_META.map((d) => [d.code, Math.max(1, Math.round((sum[d.code] / n) * 100))])
    );
  })();

  const first = chartHistory.length > 0 ? chartHistory[0].rci : 0;
  const last = chartHistory.length > 0 ? chartHistory[chartHistory.length - 1].rci : 0;
  const delta = last - first;

  if (loading) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="profile-screen" style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh" }}>
          <span style={{ fontSize:13, color:P.textMute, fontWeight:300 }}>Loading...</span>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{STYLES}</style>
      <div className="profile-screen">

        <div style={{
          position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)",
          width: 420, height: 320, pointerEvents: "none", zIndex: 0,
          background: "radial-gradient(ellipse at 40% 0%, rgba(158,132,86,0.08) 0%, transparent 65%)",
        }} />

        <div style={{ position: "relative", zIndex: 1, padding: "max(54px, calc(env(safe-area-inset-top) + 12px)) 24px var(--nav-clearance)" }}>

          {/* Header */}
          <div className="fu d1" style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: 28,
          }}>
            <Wordmark style={{ marginBottom: 0 }} />
            <div style={{ fontSize: 10, color: P.primary, border: `1px solid ${P.border}`,
              padding: "4px 11px", borderRadius: 99, letterSpacing: "0.07em", fontWeight: 500 }}>
              Day {totalSnapshots}
            </div>
          </div>

          {/* User Identity */}
          <div className="fu d1" style={{
            display: "flex", alignItems: "center", gap: 16, marginBottom: 28,
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%", flexShrink: 0,
              background: P.primarySoft,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ fontFamily: "'Playfair Display', serif",
                fontSize: 20, fontStyle: "italic", color: P.primary }}>
                {initials}
              </span>
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                <div style={{ fontFamily: "'Playfair Display', serif",
                  fontSize: 22, fontStyle: "italic", color: P.text,
                  lineHeight: 1.2 }}>
                  {fullName}
                </div>
                {(user?.subscription_status === "founding" ||
                  user?.subscription_status === "founding_comp") && (
                  <div style={{
                    fontSize: 10,
                    color: "#b8860b",
                    background: "rgba(184, 134, 11, 0.08)",
                    padding: "4px 9px",
                    borderRadius: 12,
                    fontWeight: 500,
                    letterSpacing: "0.05em",
                    whiteSpace: "nowrap",
                  }}>
                    Founding Member
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: P.textMute, fontWeight: 300 }}>
                {email}
              </div>
            </div>
          </div>

          {/* Stat Cards */}
          <div className="fu d2" style={{
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10, marginBottom: 12,
          }}>
            {[
              { label: "Check-ins",  value: totalSnapshots },
              { label: "Day streak", value: streak },
              { label: "Member for", value: `${memberDays}d` },
            ].map((stat, i) => (
              <div key={i} style={{
                background: P.surface, borderRadius: 16,
                padding: "16px 12px", textAlign: "center",
                boxShadow: "0 1px 12px rgba(28,24,20,0.07)",
              }}>
                <div style={{ fontFamily: "'Playfair Display', serif",
                  fontSize: 26, fontStyle: "italic", color: P.text,
                  lineHeight: 1, marginBottom: 5 }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 9, color: P.textMute,
                  letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 500 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Refer a Friend — founding-cohort perk. Fetches the user's
              personal referral code on mount and renders nothing for
              organic users (null code). */}
          <ReferAFriend token={token} />

          {/* RCI History */}
          {chartHistory.length > 1 && (
            <div className="fu d3 section-card">
              <div style={{ display: "flex", justifyContent: "space-between",
                alignItems: "center", marginBottom: 6 }}>
                <div className="section-label" style={{ marginBottom: 0 }}>Signal History</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%",
                    background: delta >= 0 ? P.primary : P.textMute }} />
                  <span style={{ fontSize: 11, color: P.textMid }}>
                    {delta >= 0 ? `+${delta}` : delta} over {chartHistory.length} days
                  </span>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <RCIChart history={chartHistory} />
              </div>
            </div>
          )}

          {/* Domains — replaces the old "Domain Averages" bar card in
              place. Profile is now the parent surface for the per-domain
              detail screens, which lost their bottom-nav tab when
              Journal took that slot, so each row has to actually go
              somewhere: tapping routes to /domain/:code.

              Reuses DomainDots (the dot language shared with
              DomainSubScores on Signal/Reveal) rather than the old
              bespoke progress bars, and carries DomainInfoButton per
              row — its first placement outside the near-dead /coherence
              route. Not DomainSubScores wholesale: that component
              renders its own card chrome (which would nest inside
              .section-card) and its third column is a plain-language
              label from the insights API that Profile doesn't fetch,
              so it would render three em-dashes. The dot row is the
              part that transfers. */}
          <div className="fu d4 section-card">
            <div className="section-label">Domains</div>
            {DOMAIN_META.map((domain, i) => {
              const avg = domainAvg[domain.code];
              return (
                <button
                  key={domain.code}
                  type="button"
                  onClick={() => navigate(`/domain/${domain.key}`)}
                  aria-label={`Open ${domain.display} detail`}
                  style={{
                    all: "unset", cursor: "pointer", boxSizing: "border-box",
                    display: "flex", alignItems: "center",
                    justifyContent: "space-between", width: "100%",
                    paddingBottom: i < DOMAIN_META.length - 1 ? 14 : 0,
                    marginBottom: i < DOMAIN_META.length - 1 ? 14 : 0,
                    borderBottom: i < DOMAIN_META.length - 1
                      ? `1px solid ${P.border}` : "none",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: 10,
                      background: P.primarySoft, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <span style={{ fontSize: 10, color: P.primary,
                        letterSpacing: "0.08em", fontWeight: 500 }}>
                        {domain.code}
                      </span>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        display: "flex", alignItems: "center",
                        fontSize: 13, color: P.text, fontWeight: 400,
                        marginBottom: 6,
                      }}>
                        {DOMAIN_WORD[domain.code] || domain.code}
                        {/* Stops the row's navigation: the ⓘ opens the
                            explainer sheet, it isn't a second way into
                            the detail screen. */}
                        <span
                          onClick={(e) => e.stopPropagation()}
                          style={{ display: "inline-flex" }}
                        >
                          <DomainInfoButton domain={domain.key} />
                        </span>
                      </div>
                      <DomainDots score={avg / 100} />
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <span style={{ fontFamily: "'Playfair Display', serif",
                      fontSize: 22, fontStyle: "italic", color: P.text }}>
                      {avg}
                    </span>
                    <span style={{ fontSize: 15, color: P.textMute }}>›</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Signal History card — Profile's lightweight entry point
              to the day-by-day timeline. White surface to contrast with
              the dark Dashboard version; same data source. Always
              renders (no gating) so the link is always discoverable
              from Profile regardless of check-in count. */}
          {totalSnapshots > 0 && (() => {
            // history payload is newest-first from the API; reverse to
            // chronological for the sparkline so it reads left → right.
            const sparkValues = historyRaw
              .slice(0, 7)
              .map(s => s?.rci_total)
              .reverse();
            const showSpark = sparkValues.filter(
              v => typeof v === "number" && Number.isFinite(v)
            ).length >= 2;
            return (
              <button
                type="button"
                onClick={() => navigate("/history")}
                aria-label={`Open signal history — ${totalSnapshots} ${totalSnapshots === 1 ? "check-in" : "check-ins"} recorded`}
                style={{
                  all: "unset", display: "block", width: "100%",
                  boxSizing: "border-box", cursor: "pointer",
                  background: "white",
                  border: `0.5px solid ${P.border}`,
                  borderRadius: 14,
                  padding: 14,
                  marginBottom: 16,
                }}
              >
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "center", gap: 12,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontSize: 10, color: P.primary, fontWeight: 500,
                      letterSpacing: "0.08em", margin: "0 0 6px",
                    }}>
                      SIGNAL HISTORY
                    </p>
                    <p style={{
                      fontSize: 14, color: P.text, fontWeight: 500,
                      margin: "0 0 8px",
                    }}>
                      {totalSnapshots} {totalSnapshots === 1 ? "check-in" : "check-ins"} recorded
                    </p>
                    {showSpark && (
                      <Sparkline
                        data={sparkValues}
                        width={80}
                        height={16}
                        color={P.primary}
                        showDot={false}
                      />
                    )}
                  </div>
                  <div style={{
                    width: 32, height: 32, borderRadius: "50%",
                    border: `1.5px solid ${P.primary}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke={P.primary} strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M13 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            );
          })()}

          {/* Data connections — hub for wearable / health integrations.
              Tinted card treatment differentiates it from the flat
              Notifications/Account sections below. Oura row navigates
              to /wearables for sync history + reconnect/disconnect. */}
          <div className="fu d5 data-connections-card">
            <div className="section-label">Data connections</div>

            {DATA_CONNECTION_ROWS(wearableProviders, ouraStatus, isOuraUIEnabled()).map((p) => (
              <button
                key={p.key}
                type="button"
                className="data-row"
                onClick={() => navigate(p.key === "fitbit" ? "/wearables#fitbit" : "/wearables")}
                aria-label={p.needs_reconnect ? `Reconnect ${p.label}` : `Manage ${p.label} connection`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: P.primarySoft, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke={P.primary} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
                      data-icon={providerIconKey(p.key)}>
                      {PROVIDER_ICON[providerIconKey(p.key)]}
                    </svg>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: P.text, fontWeight: 400, marginBottom: 2 }}>
                      {p.label}
                    </div>
                    <div style={{ fontSize: 11.5, color: P.textMute, fontWeight: 300 }}>
                      {providerMetrics(p.key)}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                  {p.needs_reconnect ? (
                    <span className="status-pill pill-warn">
                      <span className="dot" />
                      Reconnect needed
                    </span>
                  ) : p.connected ? (
                    <span className="status-pill pill-connected">
                      <span className="dot" />
                      Connected
                    </span>
                  ) : (
                    <span className="status-pill pill-muted">
                      <span className="dot" />
                      Not connected
                    </span>
                  )}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke={P.textMute} strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round"
                    aria-hidden="true">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </button>
            ))}

            {/* "+ Add wearable" — routes to the Wearables hub where all
                connect/manage cards live (rather than a dead-end alert).
                Future integrations (Apple Health, Garmin) surface there. */}
            <button
              type="button"
              className="data-row data-row-add"
              onClick={() => navigate("/wearables")}
            >
              <span>+ Add wearable</span>
            </button>
          </div>

          {/* Notifications — daily check-in reminder is wired to the
              real Web Push subscription; the other two are local-only
              prefs persisted via localStorage. Daily-checkin row falls
              back to a static message when the browser permission is
              'denied' (only recoverable through site settings) or the
              browser doesn't support Web Push at all. */}
          <div className="fu d6 section-card">
            <div className="section-label">Notifications</div>

            {/* Daily check-in reminder */}
            <div className="notif-row">
              <div>
                <div style={{ fontSize: 13.5, color: P.text,
                  fontWeight: 400, marginBottom: 2 }}>
                  Daily check-in reminder
                </div>
                <div style={{ fontSize: 11.5, color: P.textMute,
                  fontWeight: 300 }}>
                  {pushPermission === 'denied'
                    ? "Notifications blocked in browser settings"
                    : pushPermission === 'unsupported'
                      ? "Not available on this device"
                      : pushPermission === 'install-required'
                        ? "Add Haiven to your Home Screen first (Share → Add to Home Screen), then try again"
                        : "Push reminder at your morning band"}
                </div>
              </div>
              {pushPermission === 'denied' || pushPermission === 'unsupported' ? (
                <span style={{ fontSize: 11, color: P.textMute, fontWeight: 300 }}>—</span>
              ) : pushPermission === 'install-required' ? (
                <button
                  type="button"
                  onClick={() => setPushPermission('default')}
                  style={{
                    background: "none", border: "none",
                    color: P.primary, fontSize: 11.5, fontWeight: 500,
                    cursor: "pointer", padding: "6px 4px",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Retry
                </button>
              ) : (
                <Toggle
                  value={notifs.checkin}
                  onChange={onCheckinToggle}
                  disabled={pushBusy}
                />
              )}
            </div>

            {/* Reminder times — three independent bands (morning /
                midday / evening), each row is a toggle + time picker.
                The cron evaluates these against the user's IANA
                timezone (read-only below).
                Render gate relaxed May 27 2026: previously hidden
                until ``notifs.checkin && pushPermission==='granted'``,
                which made the section impossible to discover before
                opting into push (you'd need to subscribe with no
                ability to pre-set your times). Now visible whenever
                the browser supports push at all; a banner inside
                explains the "subscribe first" relationship when push
                isn't yet on. */}
            {pushPermission !== 'unsupported' && pushPermission !== 'denied' && (
              <div style={{
                marginLeft: 0,
                marginTop: 14,
                paddingLeft: 14,
                borderLeft: `2px solid ${P.border}`,
              }}>
                {(!notifs.checkin || pushPermission !== 'granted') && (
                  <div style={{
                    fontSize: 11.5, color: P.textMute, fontWeight: 300,
                    lineHeight: 1.5, margin: '0 0 10px',
                  }}>
                    Set your preferred times now — reminders will start
                    firing once you turn on the daily check-in reminder
                    above.
                  </div>
                )}
                <div style={{
                  fontSize: 11, color: P.textMute, fontWeight: 400,
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                  marginBottom: 10,
                }}>
                  Reminder times
                </div>

                {[
                  { key: 'morning', label: 'Morning' },
                  { key: 'midday',  label: 'Midday' },
                  { key: 'evening', label: 'Evening' },
                ].map(({ key, label }) => {
                  const enabledKey = `${key}_enabled`;
                  const timeKey = `${key}_time`;
                  return (
                    <div key={key} style={{
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 0',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Toggle
                          value={reminders[enabledKey]}
                          onChange={v => setReminders(prev => ({ ...prev, [enabledKey]: v }))}
                        />
                        <span style={{ fontSize: 13.5, color: P.text, fontWeight: 400 }}>
                          {label}
                        </span>
                      </div>
                      <input
                        type="time"
                        value={reminders[timeKey]}
                        disabled={!reminders[enabledKey]}
                        onChange={e => setReminders(prev => ({
                          ...prev, [timeKey]: e.target.value,
                        }))}
                        style={{
                          fontSize: 13, padding: '6px 8px',
                          border: `1px solid ${P.border}`, borderRadius: 8,
                          background: P.surface, color: P.text,
                          opacity: reminders[enabledKey] ? 1 : 0.4,
                          fontFamily: 'DM Sans, sans-serif',
                        }}
                      />
                    </div>
                  );
                })}

                <div style={{
                  fontSize: 11, color: P.textFaint, fontWeight: 300,
                  marginTop: 8, lineHeight: 1.5,
                }}>
                  Times are in your timezone
                  {reminders.timezone ? ` (${reminders.timezone})` : ''}.
                  Reminders only fire if you haven't checked in yet that day.
                </div>

                <div style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'flex-end', gap: 10,
                  marginTop: 10,
                }}>
                  {remindersSaveMsg && (
                    <span style={{
                      fontSize: 11,
                      color: remindersSaveMsg === 'Saved' ? P.text : P.textMute,
                      fontWeight: 400,
                    }}>
                      {remindersSaveMsg}
                    </span>
                  )}
                  <button
                    onClick={onSaveReminders}
                    disabled={!remindersDirty || remindersSaving}
                    style={{
                      fontSize: 12, fontWeight: 500,
                      padding: '7px 14px', borderRadius: 8,
                      border: 'none',
                      background: (!remindersDirty || remindersSaving)
                        ? P.surface : P.primary,
                      color: (!remindersDirty || remindersSaving)
                        ? P.textFaint : '#fff',
                      cursor: (!remindersDirty || remindersSaving)
                        ? 'default' : 'pointer',
                      fontFamily: 'DM Sans, sans-serif',
                      transition: 'background 0.15s',
                    }}
                  >
                    {remindersSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            )}

            {/* New insights available — local-only pref */}
            <div className="notif-row">
              <div>
                <div style={{ fontSize: 13.5, color: P.text,
                  fontWeight: 400, marginBottom: 2 }}>
                  New insights available
                </div>
                <div style={{ fontSize: 11.5, color: P.textMute,
                  fontWeight: 300 }}>
                  When Haiven surfaces a pattern
                </div>
              </div>
              <Toggle
                value={notifs.insights}
                onChange={v => setNotifs(prev => ({ ...prev, insights: v }))}
              />
            </div>

            {/* Protocol nudges — local-only pref */}
            <div className="notif-row">
              <div>
                <div style={{ fontSize: 13.5, color: P.text,
                  fontWeight: 400, marginBottom: 2 }}>
                  Protocol nudges
                </div>
                <div style={{ fontSize: 11.5, color: P.textMute,
                  fontWeight: 300 }}>
                  Reminders for active protocols
                </div>
              </div>
              <Toggle
                value={notifs.protocol}
                onChange={v => setNotifs(prev => ({ ...prev, protocol: v }))}
              />
            </div>
          </div>

          {/* Care team — powers the pre-session share nudge on Home.
              Two fields only: "I see a provider" + which day sessions
              land. No provider identity, no directory — the lightest
              honest version of the June pre-session-moment spec. */}
          <div className="fu d6 section-card">
            <div className="section-label">Sharing reminders</div>
            <div className="notif-row">
              <div>
                <div style={{ fontSize: 13.5, color: P.text,
                  fontWeight: 400, marginBottom: 2 }}>
                  Remind me to share a summary
                </div>
                <div style={{ fontSize: 11.5, color: P.textMute,
                  fontWeight: 300 }}>
                  Useful if you check in with someone regularly, like a friend, coach or mentor
                </div>
              </div>
              <Toggle value={seesProvider} onChange={onSeesProviderToggle} />
            </div>
            {seesProvider && (
              <div className="notif-row">
                <div>
                  <div style={{ fontSize: 13.5, color: P.text,
                    fontWeight: 400, marginBottom: 2 }}>
                    Reminder day
                  </div>
                  <div style={{ fontSize: 11.5, color: P.textMute,
                    fontWeight: 300 }}>
                    We'll offer to share your latest summary that day
                  </div>
                </div>
                <select
                  value={sessionDay === "" ? "" : String(sessionDay)}
                  onChange={e => onSessionDayChange(e.target.value)}
                  aria-label="Reminder day"
                  style={{
                    fontSize: 13, padding: "6px 8px",
                    border: `1px solid ${P.border}`, borderRadius: 8,
                    background: P.surface, color: P.text,
                    fontFamily: "DM Sans, sans-serif",
                  }}
                >
                  <option value="">Not set</option>
                  {WEEKDAYS.map((day, i) => (
                    <option key={day} value={i}>{day}s</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* AI & privacy — the AI data-sharing consent toggle
              (5.1.1(i)/5.1.2(i)). Turning it off takes effect
              immediately: the backend re-reads the flag on every
              request, so no further data reaches Anthropic from the
              moment the PATCH lands. Turning it off routes through the
              honest what-changes explainer first. */}
          <div className="fu d6 section-card">
            <div className="section-label">AI &amp; privacy</div>
            <div className="notif-row">
              <div>
                <div style={{ fontSize: 13.5, color: P.text,
                  fontWeight: 400, marginBottom: 2 }}>
                  AI data sharing
                </div>
                <div style={{ fontSize: 11.5, color: P.textMute,
                  fontWeight: 300, maxWidth: 230 }}>
                  Sends your reflections, scores, and wearable-derived
                  labels to Anthropic (Claude) to power insights, Deep
                  Dive, and chat. Never your name or account details.
                </div>
              </div>
              <Toggle
                value={user?.ai_data_consent === true}
                onChange={onAIConsentToggle}
              />
            </div>

            {/* Research use. The privacy policy promises this choice is
                changeable in Settings — this row is what makes that
                true. NULL (never asked) renders OFF. */}
            <div className="notif-row">
              <div>
                <div style={{ fontSize: 13.5, color: P.text,
                  fontWeight: 400, marginBottom: 2 }}>
                  Research use
                </div>
                <div style={{ fontSize: 11.5, color: P.textMute,
                  fontWeight: 300, maxWidth: 230 }}>
                  Lets Haiven use your de-identified signal for research
                  and product improvement. Also required to include written
                  entries in anything you choose to share.
                  {user?.research_consent == null && " Not set yet."}
                </div>
              </div>
              <Toggle
                value={user?.research_consent === true}
                onChange={onResearchConsentToggle}
                disabled={researchConsentSaving}
              />
            </div>
          </div>
          {aiDeclineConfirming && (
            <AIDeclineExplainer
              submitting={aiConsentSaving}
              onConfirm={confirmAIConsentOff}
              onBack={() => setAiDeclineConfirming(false)}
            />
          )}

          {/* Account */}
          <div className="fu d7 section-card">
            <div className="section-label">Account</div>
            <button className="btn-logout" onClick={onLogout}>Sign out</button>
            {deleteStep > 0 ? (
              <div style={{ textAlign: "center", padding: "4px 0 2px" }}>
                <p style={{ fontSize: 13.5, color: P.text, fontWeight: 400,
                  lineHeight: 1.5, margin: "0 0 6px" }}>
                  {deleteStep === 1
                    ? "Delete your account?"
                    : "This is permanent."}
                </p>
                <p style={{ fontSize: 12.5, color: P.textMid, fontWeight: 300,
                  lineHeight: 1.6, margin: "0 0 10px" }}>
                  {deleteStep === 1
                    ? "This can't be undone. Your login and personal information are permanently deleted."
                    : "Once you confirm, your account can't be recovered."}
                </p>
                {deleteStep === 1 ? (
                  <button
                    className="btn-logout"
                    style={{ borderColor: P.danger, color: P.danger }}
                    onClick={() => setDeleteStep(2)}
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    className="btn-logout"
                    style={{ borderColor: P.danger, color: P.danger }}
                    disabled={deleting}
                    onClick={handleDeleteAccount}
                  >
                    {deleting ? "Deleting…" : "Delete My Account"}
                  </button>
                )}
                <button
                  className="btn-delete"
                  disabled={deleting}
                  onClick={cancelDelete}
                >
                  Cancel
                </button>
                {deleteError && (
                  <p style={{ fontSize: 11.5, color: "#C0392B", fontWeight: 300,
                    margin: "8px 0 0" }}>
                    {deleteError}
                  </p>
                )}
              </div>
            ) : (
              <button className="btn-delete" onClick={() => setDeleteStep(1)}>
                Delete account
              </button>
            )}
          </div>

          {/* Disclaimer */}
          <div className="fu d8" style={{ textAlign: "center", marginTop: 8 }}>
            <p style={{ fontSize: 11, color: P.textFaint, fontWeight: 300, lineHeight: 1.7 }}>
              Haiven does not diagnose, treat, or provide psychotherapy.
            </p>
          </div>

        </div>

        {/* Bottom Nav */}
        <BottomNav />

      </div>
    </>
  );
}
